import { useState, useCallback, useMemo, useRef } from "react";

const REQUIRED_METHODS = [
  "list",
  "findOneById",
  "create",
  "update",
  "softDelete",
  "hardDelete",
  "restore",
];

/**
 * Vérifie le service UNE SEULE FOIS
 */
function validateService(service) {
  if (!service || typeof service !== "object") {
    throw new Error("useCrudApi: service invalide (objet attendu)");
  }

  REQUIRED_METHODS.forEach((method) => {
    if (typeof service[method] !== "function") {
      throw new Error(
        `useCrudApi: la méthode "${method}" est manquante dans le service`,
      );
    }
  });
}

/**
 * @typedef {Object} CrudService
 * @property {(filtre?: any) => Promise<any[]>} list
 * @property {(id: number|string) => Promise<any>} findOneById
 * @property {(data: any) => Promise<any>} create
 * @property {(id: number|string, data: any) => Promise<any>} update
 * @property {(id: number|string) => Promise<any>} softDelete
 * @property {(id: number|string) => Promise<any>} hardDelete
 * @property {(id: number|string) => Promise<any>} restore
 */

export default function useCrudApi(service) {
  const validated = useRef(false);
  if (!validated.current) {
    validateService(service);
    validated.current = true;
  }

  const [data, setData] = useState({
    list: null,
    findOneById: null,
    create: null,
    update: null,
    softDelete: null,
    hardDelete: null,
    restore: null,
  });
  const [loading, setLoading] = useState({
    list: null,
    findOneById: null,
    create: null,
    update: null,
    softDelete: null,
    hardDelete: null,
    restore: null,
  });
  const [error, setError] = useState(null);

  const setLoadingByAction = useCallback((action, value) => {
    setLoading((prev) => ({ ...prev, [action]: value }));
  }, []);

  const run = useCallback(
    async (methodName, ...params) => {
      if (!service[methodName])
        throw new Error(`La méthode "${methodName}" n'existe pas dans le Service.${service}`);
      setLoadingByAction(methodName, true);
      setError(null);

      try {
        const res = await service[methodName](...params);
        setData((prev) => ({ ...prev, [methodName]: res }));
        return res;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoadingByAction(methodName, false);
      }
    },
    [service, setLoadingByAction],
  );

  // Méthodes CRUD
  const findAll = useCallback((...params) => run("list", ...params), [run]);
  const findOne = useCallback(
    (id, ...params) => run("findOneById", id, ...params),
    [run],
  );
  const create = useCallback((...params) => run("create", ...params), [run]);
  const update = useCallback((...params) => run("update", ...params), [run]);
  const softDelete = useCallback(
    (...params) => run("softDelete", ...params),
    [run],
  );
  const hardDelete = useCallback(
    (...params) => run("hardDelete", ...params),
    [run],
  );
  const restore = useCallback((...params) => run("restore", ...params), [run]);

  const api = useMemo(
    () => ({
      data,
      loading,
      error,
      setData,
      run,
      findAll,
      findOne,
      create,
      update,
      softDelete,
      hardDelete,
      restore,
    }),
    [
      data,
      loading,
      error,
      run,
      findAll,
      findOne,
      create,
      update,
      softDelete,
      hardDelete,
      restore,
    ],
  );

  return api;
}
